import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Flame, Target, Star } from 'lucide-react';
import { Card } from '../ui/Card';
import { StatTile } from '../ui/StatTile';
import { useMotionBudget } from '../../app/hooks/useMotionBudget';
import { WeeklyActivityDots } from '../../../components/meester-djay/WeeklyActivityDots';
import { getProgressToNextLevel } from '../../config/constants';
import { STATUS } from '../../config/designTokens';
import type { DayActivity } from '../../utils/activityHelpers';
interface GlanceBarProps {
  streakDays: number;
  dailyProgress: number;
  dailyGoalTarget: number;
  level: number;
  xp: number;
  weeklyGoal: number;
  weeklyActivity: DayActivity[];
  onNavigateToModules?: () => void;
}
export function GlanceBar({
  streakDays,
  dailyProgress,
  dailyGoalTarget,
  level,
  xp,
  weeklyGoal,
  weeklyActivity,
  onNavigateToModules
}: GlanceBarProps) {
  const {
    isReducedMotion
  } = useMotionBudget();
  // Calculate progress percentages
  const streakProgress = Math.min(100, streakDays / 7 * 100);
  const dailyGoalProgress = Math.min(100, dailyProgress / dailyGoalTarget * 100);
  // Level progress — uses the SAME `getProgressToNextLevel` helper as the
  // Sidebar/MobileNav XP bar (audit LEARNER_HOME_FLOW_UX §P1-2). The previous
  // implementation hardcoded `xp % 1000`, which drifted from the real
  // LEVEL_THRESHOLDS curve and made the two progress indicators disagree
  // about how close the learner was to the next level.
  const levelProgress = Math.min(100, getProgressToNextLevel(xp).percent);
  const isDailyGoalReached = dailyProgress >= dailyGoalTarget;
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: -10
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      scale: 1
    }
  };
  return <motion.div variants={containerVariants} initial={isReducedMotion ? 'visible' : 'hidden'} animate="visible" className="mb-6" data-id="element-1315" data-mp="1ua9hzkflio5c">
      <Card className="p-3 sm:p-4 bg-white border-2 border-slate-100 rounded-2xl shadow-sm flex flex-col gap-4 overflow-hidden" data-id="element-1316" data-mp="1lj0uj4v2wtln">
        <div className="flex items-center justify-between gap-2 sm:gap-4" data-id="element-1317" data-mp="1gkpkat0vgh2w">
          {/* Streak Segment — non-interactive */}
          <motion.div variants={itemVariants} className="flex-1" data-id="element-1318" data-mp="1bmecsoy1uinu">
            <StatTile icon={Flame} iconColorClass={streakDays > 0 ? 'text-orange-500' : 'text-slate-400'} label="dagen" value={`${streakDays}/${weeklyGoal}`} progress={streakProgress} progressColorClass={streakDays > 0 ? 'bg-orange-500' : 'bg-slate-300'} data-id="element-1319" data-mp="16lgveekv5sne" />
          </motion.div>

          <div className="w-px h-8 bg-slate-100 flex-shrink-0" data-id="element-1320" data-mp="2rzupq4se0tq7" />

          {/* Daily Goal Segment — only interactive segment (navigates to modules) */}
          <motion.div variants={itemVariants} className="flex-1" data-id="element-1321" data-mp="34h5g8dereho3">
            <StatTile icon={Target} iconColorClass={isDailyGoalReached ? STATUS.success.text : dailyProgress > 0 ? STATUS.warning.text : 'text-slate-400'} label="doel" value={`${dailyProgress}/${dailyGoalTarget}`} progress={dailyGoalProgress} progressColorClass={isDailyGoalReached ? STATUS.success.bg : STATUS.warning.bg} interactive={true} onClick={onNavigateToModules} data-id="element-1322" data-mp="34fcbj3npi7iq" />
          </motion.div>

          <div className="w-px h-8 bg-slate-100 flex-shrink-0" data-id="element-1323" data-mp="5qwzzc43io27" />

          {/* Level Segment — non-interactive */}
          <motion.div variants={itemVariants} className="flex-1" data-id="element-1324" data-mp="9rgwqekrbpnt">
            <StatTile icon={Star} iconColorClass={STATUS.info.text} label="" value={`Level ${level}`} progress={levelProgress} progressColorClass={STATUS.info.bg} data-id="element-1325" data-mp="8d624r40ndyk" />
          </motion.div>
        </div>

        {/* Weekly Activity Row */}
        <motion.div variants={itemVariants} className="pt-3 border-t border-slate-100" data-id="element-1326" data-mp="d9wilfjqgbki">
          <WeeklyActivityDots weeklyActivity={weeklyActivity} color="emerald" weeklyGoal={weeklyGoal} data-id="element-1327" data-mp="r8mg2wagsd9e" />
        </motion.div>
      </Card>
    </motion.div>;
}